import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { me, checkSession } from '../lib/session.js';
import { setMoneyDecimals } from '../lib/money.js';
import TimeInput24 from '../components/TimeInput24.jsx';

// Settings rendered as a 24-hour time field (HH:MM) instead of a text box.
// (Jam Lembur Minimal moved to per-scheme config on the Pemrosesan Gaji page.)
const TIME_SETTINGS = new Set();
const isTimeSetting = (s) => TIME_SETTINGS.has(`${s.grp}/${s.name}`);

// Pengaturan — the tm_setupall successor: system-wide configuration plus
// user (PENGGUNA) management. Settings are declared by the application and
// seeded on first start; the UI only edits values.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

export default function Pengaturan() {
  const [tab, setTab] = createSignal('umum');
  const [msg, setMsg] = createSignal(null);

  return (
    <>
      <div class="page-head">
        <h2>Pengaturan</h2>
        <div class="spacer" />
        <div class="seg">
          <button class="seg-item" classList={{ active: tab() === 'umum' }} onClick={() => { setTab('umum'); setMsg(null); }}>Umum</button>
          <Show when={me()?.role === 'superadmin'}>
            <button class="seg-item" classList={{ active: tab() === 'perusahaan' }} onClick={() => { setTab('perusahaan'); setMsg(null); }}>Perusahaan</button>
            <button class="seg-item" classList={{ active: tab() === 'pengguna' }} onClick={() => { setTab('pengguna'); setMsg(null); }}>Pengguna</button>
          </Show>
        </div>
      </div>

      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-bottom:12px">{msg().msg}</div>
      </Show>

      <Show when={tab() === 'umum'}>
        <AccountCard setMsg={setMsg} />
        <SettingsTab setMsg={setMsg} />
      </Show>
      <Show when={tab() === 'perusahaan'}><CompaniesTab setMsg={setMsg} /></Show>
      <Show when={tab() === 'pengguna'}><UsersTab setMsg={setMsg} /></Show>
    </>
  );
}

// ── Perusahaan: company master (superadmin) ────────────────────────────
function CompaniesTab(props) {
  const [list, { refetch }] = createResource(() => getJSON('/api/companies?all=1'));
  const [edit, setEdit] = createSignal(null); // company object being edited (id 0 = new)
  const [actFor, setActFor] = createSignal(null); // company whose activation panel is open

  async function save() {
    const r = await postJSON('/api/companies', edit());
    if (r.ok) {
      props.setMsg({ ok: true, msg: `Perusahaan tersimpan (id ${r.data.id}).` });
      setEdit(null);
      refetch();
    } else props.setMsg({ ok: false, msg: r.data.error });
  }

  return (
    <div class="card card-pad vg-card">
      <p class="ky-vars-note">
        Master perusahaan (IDPerusahaan). Skema, karyawan, dan pemrosesan gaji selalu
        milik tepat satu perusahaan; admin hanya melihat perusahaan yang
        ditugaskan padanya.
      </p>
      <div class="ky-vars-head">
        <h4 class="kg-hist-title">Perusahaan</h4>
        <div class="spacer" />
        <button class="btn ghost" onClick={() => { setActFor(null); setEdit({ id: 0, name: '', address: '', city: '', phone: '', active: true }); }}>
          + perusahaan
        </button>
      </div>

      <Show when={edit()}>
        <div class="pa-company-form">
          <div class="pa-company-title">{edit().id ? `Ubah perusahaan #${edit().id}` : 'Perusahaan baru'}</div>
          <p class="pa-company-hint">Nama dan alamat tampil di kepala slip gaji perusahaan ini.</p>
          <div class="pa-company-grid">
            <label class="kg-field">
              <span>Nama perusahaan</span>
              <input type="text" placeholder="mis. PT Metalindo Mojokerto" value={edit().name ?? ''}
                onInput={(e) => setEdit({ ...edit(), name: e.currentTarget.value })} />
            </label>
            <label class="kg-field">
              <span>Alamat</span>
              <input type="text" placeholder="mis. Jl. Merdeka No. 145, Dungus" value={edit().address ?? ''}
                onInput={(e) => setEdit({ ...edit(), address: e.currentTarget.value })} />
            </label>
            <label class="kg-field">
              <span>Kota / Kabupaten</span>
              <input type="text" placeholder="mis. Kab. Mojokerto" value={edit().city ?? ''}
                onInput={(e) => setEdit({ ...edit(), city: e.currentTarget.value })} />
            </label>
            <label class="kg-field">
              <span>Telepon</span>
              <input type="text" placeholder="mis. 0321 381 688" value={edit().phone ?? ''}
                onInput={(e) => setEdit({ ...edit(), phone: e.currentTarget.value })} />
            </label>
          </div>
          <div class="pa-company-row">
            <label class="ky-check">
              <input type="checkbox" checked={edit().active}
                onInput={(e) => setEdit({ ...edit(), active: e.currentTarget.checked })} />
              aktif
            </label>
            <button class="btn primary pa-company-save" onClick={save}>Simpan</button>
            <button class="btn ghost" onClick={() => setEdit(null)}>Batal</button>
          </div>
          <Show when={edit().id} fallback={
            <p class="pa-company-hint">Simpan perusahaan dulu untuk bisa mengunggah logo.</p>
          }>
            <div class="pa-company-logo">
              <span class="kg-field"><span>Logo perusahaan</span></span>
              <LogoUpload company={edit()} setMsg={props.setMsg}
                onChange={() => { refetch(); checkSession(); }} />
              <p class="pa-company-hint">Tampil di kepala kiri aplikasi (PNG/JPG/WebP, maks 2 MB).</p>
            </div>
          </Show>
        </div>
      </Show>

      <Show when={actFor()}>
        <ActivationPanel company={actFor()} setMsg={props.setMsg}
          onClose={() => setActFor(null)}
          onActivated={() => { refetch(); }} />
      </Show>

      <table class="data">
        <thead>
          <tr><th>ID</th><th>Nama</th><th>Kota</th><th>Status</th><th>Lisensi</th><th></th></tr>
        </thead>
        <tbody>
          <For each={list() ?? []}>{(c) =>
            <tr classList={{ inactive: !c.active }}>
              <td class="ky-nik">{c.id}</td>
              <td>{c.name}<Show when={c.address}><div class="kg-sub">{c.address}</div></Show></td>
              <td>{c.city}</td>
              <td><span class={`badge ${c.active ? 'success' : 'danger'}`}>{c.active ? 'aktif' : 'nonaktif'}</span></td>
              <td><span class={`badge ${c.activated ? 'success' : 'danger'}`}>{c.activated ? 'aktif' : 'belum aktif'}</span></td>
              <td class="num">
                <button class="btn ghost" onClick={() => { setEdit(null); setActFor({ ...c }); }}>aktivasi</button>
                <button class="btn ghost" onClick={() => { setActFor(null); setEdit({ ...c }); }}>ubah</button>
              </td>
            </tr>
          }</For>
        </tbody>
      </table>
    </div>
  );
}

// ActivationPanel — per-company licensing (§4j). Shows the Activation ID to copy
// and send to the developer, and a field to paste back the signed Activation Key.
// Pemrosesan gaji + login karyawan are blocked until the company is activated.
function ActivationPanel(props) {
  const [info, { refetch }] = createResource(() => getJSON(`/api/company/${props.company.id}/activation`));
  const [key, setKey] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [copied, setCopied] = createSignal(false);

  async function copyId() {
    try {
      await navigator.clipboard.writeText(info().activationId);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { props.setMsg({ ok: false, msg: 'gagal menyalin — salin manual' }); }
  }

  async function activate() {
    if (!key().trim()) return;
    setBusy(true);
    const r = await postJSON(`/api/company/${props.company.id}/activation`, { key: key().trim() });
    setBusy(false);
    if (r.ok) {
      props.setMsg({ ok: true, msg: `Perusahaan "${props.company.name}" berhasil diaktivasi.` });
      setKey(''); refetch(); props.onActivated?.();
    } else props.setMsg({ ok: false, msg: r.data.error });
  }

  return (
    <div class="pa-company-form">
      <div class="pa-company-title">Aktivasi — {props.company.name} (#{props.company.id})</div>
      <Show when={info()} fallback={<p class="pa-company-hint">Memuat…</p>}>
        <Show when={info().activated} fallback={
          <p class="kg-verdict fail" style="margin:0 0 10px">
            Belum aktif. Pemrosesan gaji dan login karyawan untuk perusahaan ini dinonaktifkan
            sampai diaktivasi.
          </p>
        }>
          <p class="kg-verdict ok" style="margin:0 0 10px">
            Sudah aktif{info().activatedAt ? ` sejak ${info().activatedAt}` : ''}.
          </p>
        </Show>

        <p class="pa-company-hint">
          Salin <b>ID Aktivasi</b> di bawah dan kirim ke pengembang. Tempel <b>Kunci Aktivasi</b>
          yang Anda terima, lalu klik Aktivasi.
        </p>

        <label class="kg-field">
          <span>ID Aktivasi (kirim ke pengembang)</span>
          <textarea class="pa-act-id" readonly rows="2" onFocus={(e) => e.currentTarget.select()}>{info().activationId}</textarea>
        </label>
        <div class="pa-company-row">
          <button class="btn ghost" onClick={copyId}>{copied() ? 'tersalin ✓' : 'Salin ID'}</button>
        </div>

        <label class="kg-field">
          <span>Kunci Aktivasi (tempel dari pengembang)</span>
          <textarea class="pa-act-id" rows="3" placeholder="tempel kunci aktivasi di sini…"
            value={key()} onInput={(e) => setKey(e.currentTarget.value)} />
        </label>
        <div class="pa-company-row">
          <button class="btn primary" disabled={busy() || !key().trim()} onClick={activate}>Aktivasi</button>
          <button class="btn ghost" onClick={() => props.onClose?.()}>Tutup</button>
        </div>
      </Show>
    </div>
  );
}

// LogoUpload — upload/replace/remove one company's logo. Local has/ver state
// drives the preview so it updates immediately after a change (the cache-bust
// query forces the browser to re-fetch the replaced image).
function LogoUpload(props) {
  const [has, setHas] = createSignal(props.company.hasLogo);
  const [ver, setVer] = createSignal(0);
  const [busy, setBusy] = createSignal(false);
  let fileInput;
  const id = props.company.id;

  async function upload(file) {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.set('file', file);
    const res = await fetch(`/api/company/${id}/logo`, { method: 'POST', body: fd });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (fileInput) fileInput.value = '';
    if (res.ok) {
      setHas(true); setVer((v) => v + 1);
      props.setMsg({ ok: true, msg: 'Logo perusahaan tersimpan.' });
      props.onChange?.();
    } else props.setMsg({ ok: false, msg: data.error || 'gagal mengunggah logo' });
  }

  async function remove() {
    if (!confirm('Hapus logo perusahaan ini?')) return;
    setBusy(true);
    const res = await fetch(`/api/company/${id}/logo`, { method: 'DELETE' });
    setBusy(false);
    if (res.ok) {
      setHas(false);
      props.setMsg({ ok: true, msg: 'Logo dihapus.' });
      props.onChange?.();
    } else props.setMsg({ ok: false, msg: (await res.json().catch(() => ({}))).error || 'gagal' });
  }

  return (
    <div class="pa-logo">
      <Show when={has()} fallback={<div class="pa-logo-empty">belum ada logo</div>}>
        <img class="pa-logo-preview" src={`/api/company/${id}/logo?v=${ver()}`} alt="logo perusahaan" />
      </Show>
      <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.webp" disabled={busy()}
        onInput={(e) => upload(e.currentTarget.files[0])} />
      <Show when={has()}>
        <button class="btn ghost" disabled={busy()} onClick={remove}>Hapus logo</button>
      </Show>
    </div>
  );
}

// AccountCard — self-service password change for the CURRENT admin. Shown in
// the Umum tab (visible to every admin), so a non-superadmin can change their
// own password without seeing or touching other accounts. The current password
// is required (verified server-side).
function AccountCard(props) {
  const [cur, setCur] = createSignal('');
  const [p1, setP1] = createSignal('');
  const [p2, setP2] = createSignal('');
  const [busy, setBusy] = createSignal(false);

  async function submit() {
    if (p1().length < 8) { props.setMsg({ ok: false, msg: 'Password baru minimal 8 karakter.' }); return; }
    if (p1() !== p2()) { props.setMsg({ ok: false, msg: 'Konfirmasi password tidak cocok.' }); return; }
    setBusy(true);
    const r = await postJSON('/api/me/password', { currentPassword: cur(), newPassword: p1() });
    setBusy(false);
    if (r.ok) {
      props.setMsg({ ok: true, msg: 'Password Anda berhasil diganti.' });
      setCur(''); setP1(''); setP2('');
    } else props.setMsg({ ok: false, msg: r.data.error });
  }

  return (
    <div class="card card-pad pa-account">
      <h4 class="kg-hist-title">Akun Saya</h4>
      <p class="ky-vars-note">
        Ganti password akun <b>{me()?.username}</b>. Masukkan password lama untuk konfirmasi.
      </p>
      <div class="pa-new-user">
        <input type="password" autocomplete="current-password" placeholder="password lama"
          value={cur()} onInput={(e) => setCur(e.currentTarget.value)} />
        <input type="password" autocomplete="new-password" placeholder="password baru (min. 8)"
          value={p1()} onInput={(e) => setP1(e.currentTarget.value)} />
        <input type="password" autocomplete="new-password" placeholder="ulangi password baru"
          value={p2()} onInput={(e) => setP2(e.currentTarget.value)} />
        <button class="btn primary" disabled={busy() || !cur() || !p1()} onClick={submit}>Ganti Password</button>
      </div>
    </div>
  );
}

// ── Umum: grouped settings ─────────────────────────────────────────────
function SettingsTab(props) {
  const [settings, { refetch }] = createResource(() => getJSON('/api/settings'));
  const groups = createMemo(() => {
    const m = new Map();
    for (const s of settings() ?? []) {
      if (s.grp === 'Derivasi') continue; // shown in the dedicated Parameter Derivasi card below
      if (s.grp === 'Slip' && s.name === 'TampilkanSlipLama') continue; // retired (legacy Payroll-Lama)
      if (!m.has(s.grp)) m.set(s.grp, []);
      m.get(s.grp).push(s);
    }
    return [...m.entries()];
  });

  async function save(s, value) {
    if (value === s.value) return;
    const r = await postJSON('/api/settings', { grp: s.grp, name: s.name, value });
    props.setMsg(r.ok
      ? { ok: true, msg: `${s.grp} / ${s.name} tersimpan.` }
      : { ok: false, msg: r.data.error });
    // Money-decimal changes take effect across the whole app immediately.
    if (r.ok && s.grp === 'Tampilan' && s.name === 'DesimalUang') setMoneyDecimals(value);
    refetch();
  }

  return (
    <div class="pa-groups">
      <For each={groups()}>{([grp, items]) =>
        <div class="card card-pad pa-group">
          <h4 class="kg-hist-title">{grp}</h4>
          <For each={items}>{(s) =>
            <div class="pa-setting">
              <div class="pa-setting-info">
                <div class="pa-setting-name">{s.name}</div>
                <Show when={s.note}><div class="pa-setting-note">{s.note}</div></Show>
              </div>
              {/* Time settings → 24h field; Ya/Tidak → checkbox; else text. */}
              <Show when={isTimeSetting(s)} fallback={
                <Show when={['ya', 'tidak'].includes((s.value || '').trim().toLowerCase())} fallback={
                  <input
                    class="pa-setting-value"
                    type="text"
                    value={s.value}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    onBlur={(e) => save(s, e.currentTarget.value)}
                  />
                }>
                  <input
                    type="checkbox"
                    class="pa-setting-toggle"
                    checked={(s.value || '').trim().toLowerCase() === 'ya'}
                    onInput={(e) => save(s, e.currentTarget.checked ? 'Ya' : 'Tidak')}
                  />
                </Show>
              }>
                <TimeInput24 class="pa-setting-value" value={s.value} onChange={(v) => save(s, v)} />
              </Show>
            </div>
          }</For>
        </div>
      }</For>
      <DerivParamsCard setMsg={props.setMsg} />
    </div>
  );
}

// DerivParamsCard — the attendance/lembur derivation rules. Numeric rules (those
// with a settings `name`) are EDITABLE here; descriptive rules stay read-only.
function DerivParamsCard(props) {
  const [groups, { refetch }] = createResource(() => getJSON('/api/derivation-params').catch(() => []));

  async function save(p, value) {
    if (value === p.value) return;
    const r = await postJSON('/api/settings', { grp: 'Derivasi', name: p.name, value });
    props.setMsg?.(r.ok
      ? { ok: true, msg: `Parameter "${p.label}" tersimpan.` }
      : { ok: false, msg: r.data.error || 'gagal' });
    if (r.ok) refetch();
  }

  return (
    <div class="card card-pad pa-group">
      <h4 class="kg-hist-title">Parameter Derivasi</h4>
      <p class="pa-setting-note" style="margin:2px 0 12px">
        Aturan yang dipakai sistem untuk menafsirkan scan absensi & lembur menjadi jumlah
        hari/jam. Nilai numerik bisa diubah di sini — <b>mengubahnya mengubah hasil absensi
        & lembur untuk semua perusahaan</b>. Baris tanpa input bersifat tetap (logika sistem).
      </p>
      <For each={groups() ?? []}>{(g) =>
        <div style="margin-bottom:10px">
          <div class="kg-sub" style="font-weight:600;margin:6px 0">{g.title}</div>
          <For each={g.params}>{(p) =>
            <div class="pa-setting">
              <div class="pa-setting-info">
                <div class="pa-setting-name">{p.label}</div>
                <div class="pa-setting-note">{p.desc}</div>
              </div>
              <Show when={p.name} fallback={
                <div class="kg-meta-text" style="text-align:right;max-width:300px">{p.value}</div>
              }>
                <div class="pa-deriv-edit">
                  <input class="pa-setting-value pa-deriv-input" type="text" value={p.value}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    onBlur={(e) => save(p, e.currentTarget.value.trim())} />
                  <Show when={p.unit}><span class="pa-deriv-unit">{p.unit}</span></Show>
                </div>
              </Show>
            </div>
          }</For>
        </div>
      }</For>
    </div>
  );
}

// ── Pengguna: admin accounts ───────────────────────────────────────────
function UsersTab(props) {
  const [users, { refetch }] = createResource(() => getJSON('/api/users'));
  const [allCompanies] = createResource(() => getJSON('/api/companies?all=1'));
  const [allSchemes] = createResource(() => getJSON('/api/schemes/all'));
  const [assignments, { refetch: refetchAssign }] = createResource(() => getJSON('/api/users/companies'));
  const [schemeAssign, { refetch: refetchSchemes }] = createResource(() => getJSON('/api/users/schemes'));
  const [adding, setAdding] = createSignal(false);
  const [nu, setNu] = createSignal({ username: '', displayName: '', password: '' });
  const [pwReset, setPwReset] = createSignal(null); // {u, p1, p2}

  const assignedTo = (uid) => assignments()?.[String(uid)] ?? [];
  const schemesOf = (uid) => schemeAssign()?.[String(uid)] ?? [];
  // Schemes the admin could be scoped to = those in the companies they manage.
  const companySchemes = (uid) =>
    (allSchemes() ?? []).filter((s) => assignedTo(uid).includes(s.companyId));

  async function toggleCompany(u, cid) {
    const cur = assignedTo(u.id);
    const next = cur.includes(cid) ? cur.filter(x => x !== cid) : [...cur, cid];
    const r = await postJSON(`/api/users/${u.id}/companies`, { companies: next });
    props.setMsg(r.ok
      ? { ok: true, msg: `Penugasan perusahaan ${u.username} diperbarui.` }
      : { ok: false, msg: r.data.error });
    refetchAssign();
  }

  async function toggleScheme(u, code) {
    const cur = schemesOf(u.id);
    const next = cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code];
    const r = await postJSON(`/api/users/${u.id}/schemes`, { schemes: next });
    props.setMsg(r.ok
      ? { ok: true, msg: `Penugasan skema ${u.username} diperbarui.` }
      : { ok: false, msg: r.data.error });
    refetchSchemes();
  }

  async function deleteUser(u) {
    if (!confirm(`Hapus pengguna ${u.username} permanen? Tindakan ini tidak bisa dibatalkan.`)) return;
    const res = await fetch(`/api/users/${u.id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    props.setMsg(res.ok
      ? { ok: true, msg: `Pengguna ${u.username} dihapus.` }
      : { ok: false, msg: data.error || 'gagal menghapus' });
    if (res.ok) { refetch(); refetchAssign(); refetchSchemes(); }
  }

  async function toggleRole(u) {
    const next = u.role === 'superadmin' ? 'admin' : 'superadmin';
    if (!confirm(`Jadikan ${u.username} ${next}?`)) return;
    const r = await postJSON(`/api/users/${u.id}/role`, { role: next });
    props.setMsg(r.ok
      ? { ok: true, msg: `${u.username} sekarang ${next}.` }
      : { ok: false, msg: r.data.error });
    refetch();
  }

  async function createUser() {
    const r = await postJSON('/api/users', nu());
    if (r.ok) {
      props.setMsg({ ok: true, msg: `Pengguna ${nu().username} dibuat.` });
      setAdding(false);
      setNu({ username: '', displayName: '', password: '' });
      refetch();
    } else props.setMsg({ ok: false, msg: r.data.error });
  }

  async function submitPwReset() {
    const r = pwReset();
    if (r.p1.length < 8) { props.setMsg({ ok: false, msg: 'Password minimal 8 karakter.' }); return; }
    if (r.p1 !== r.p2) { props.setMsg({ ok: false, msg: 'Konfirmasi password tidak cocok.' }); return; }
    const res = await postJSON(`/api/users/${r.u.id}/password`, { password: r.p1 });
    props.setMsg(res.ok
      ? { ok: true, msg: `Password ${r.u.username} diganti.` }
      : { ok: false, msg: res.data.error });
    if (res.ok) setPwReset(null);
  }

  async function toggleActive(u) {
    if (u.active && !confirm(`Nonaktifkan ${u.username}? Sesi loginnya akan diputus.`)) return;
    const r = await postJSON(`/api/users/${u.id}/active`, { active: !u.active });
    props.setMsg(r.ok
      ? { ok: true, msg: `${u.username} ${u.active ? 'dinonaktifkan' : 'diaktifkan'}.` }
      : { ok: false, msg: r.data.error });
    refetch();
  }

  return (
    <div class="card card-pad vg-card">
      <p class="ky-vars-note">
        Akun <b>pengguna</b> (admin/HR) untuk sistem ini. Konsep ini terpisah dari
        karyawan — portal karyawan memakai kredensial sendiri.
      </p>
      <div class="ky-vars-head">
        <h4 class="kg-hist-title">Akun</h4>
        <div class="spacer" />
        <button class="btn ghost" onClick={() => setAdding(!adding())}>
          {adding() ? 'batal' : '+ pengguna'}
        </button>
      </div>

      <Show when={adding()}>
        <div class="pa-new-user">
          <input type="text" placeholder="username" value={nu().username}
            onInput={(e) => setNu({ ...nu(), username: e.currentTarget.value })} />
          <input type="text" placeholder="nama tampilan" value={nu().displayName}
            onInput={(e) => setNu({ ...nu(), displayName: e.currentTarget.value })} />
          <input type="password" placeholder="password (min. 8)" value={nu().password}
            onInput={(e) => setNu({ ...nu(), password: e.currentTarget.value })} />
          <button class="btn primary" onClick={createUser}>Simpan</button>
        </div>
      </Show>

      <Show when={pwReset()}>
        <div class="pa-new-user">
          <span class="kg-meta-text">Password baru untuk <b>{pwReset().u.username}</b>:</span>
          <input type="password" placeholder="password baru (min. 8)" value={pwReset().p1}
            onInput={(e) => setPwReset({ ...pwReset(), p1: e.currentTarget.value })} />
          <input type="password" placeholder="ulangi password" value={pwReset().p2}
            onInput={(e) => setPwReset({ ...pwReset(), p2: e.currentTarget.value })} />
          <button class="btn primary" onClick={submitPwReset}>Ganti</button>
          <button class="btn ghost" onClick={() => setPwReset(null)}>Batal</button>
        </div>
      </Show>

      <div class="tbl-scroll">
      <table class="data">
        <thead>
          <tr><th>Username</th><th>Nama</th><th>Peran</th><th>Perusahaan</th><th>Skema</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <For each={users() ?? []}>{(u) =>
            <tr classList={{ inactive: !u.active }}>
              <td class="ky-nik">{u.username}<Show when={u.id === me()?.id}> <span class="badge info">Anda</span></Show></td>
              <td>{u.displayName}<div class="kg-sub">{u.lastLogin ? 'login ' + u.lastLogin : 'belum pernah login'}</div></td>
              <td>
                <span class={`badge ${u.role === 'superadmin' ? 'warning' : 'info'}`}>{u.role}</span>
              </td>
              <td>
                <Show when={u.role !== 'superadmin'} fallback={<span class="vg-dim">semua</span>}>
                  <div class="pa-assign">
                    <For each={allCompanies() ?? []}>{(c) =>
                      <label class="ky-check" title={c.name}>
                        <input type="checkbox"
                          checked={assignedTo(u.id).includes(c.id)}
                          onInput={() => toggleCompany(u, c.id)} />
                        {c.id}
                      </label>
                    }</For>
                  </div>
                </Show>
              </td>
              <td>
                <Show when={u.role !== 'superadmin'} fallback={<span class="vg-dim">semua</span>}>
                  <div class="pa-assign">
                    <For each={companySchemes(u.id)}>{(sc) =>
                      <label class="ky-check" title={`${sc.company} — ${sc.name}`}>
                        <input type="checkbox"
                          checked={schemesOf(u.id).includes(sc.code)}
                          onInput={() => toggleScheme(u, sc.code)} />
                        {sc.code}
                      </label>
                    }</For>
                    {/* "" sentinel — also show employees that have no scheme yet. */}
                    <label class="ky-check" title="Lihat juga karyawan yang belum punya skema">
                      <input type="checkbox"
                        checked={schemesOf(u.id).includes('')}
                        onInput={() => toggleScheme(u, '')} />
                      Belum ada skema
                    </label>
                  </div>
                  <Show when={schemesOf(u.id).length === 0}>
                    <div class="kg-sub">semua skema (belum dibatasi)</div>
                  </Show>
                </Show>
              </td>
              <td><span class={`badge ${u.active ? 'success' : 'danger'}`}>{u.active ? 'aktif' : 'nonaktif'}</span></td>
              <td class="num ky-var-actions">
                <button class="btn ghost" onClick={() => { setPwReset({ u, p1: '', p2: '' }); props.setMsg(null); }}>password</button>
                <Show when={u.id !== me()?.id}>
                  <button class="btn ghost" onClick={() => toggleRole(u)}>{u.role === 'superadmin' ? '→ admin' : '→ super'}</button>
                  <button class="btn ghost" onClick={() => toggleActive(u)}>{u.active ? 'nonaktifkan' : 'aktifkan'}</button>
                  <button class="btn ghost danger" onClick={() => deleteUser(u)}>hapus</button>
                </Show>
              </td>
            </tr>
          }</For>
        </tbody>
      </table>
      </div>
    </div>
  );
}
